import _ from 'lodash';
import { useSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
// @mui
import {
  Badge,
  Box,
  Divider,
  IconButton,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
// utils
import cssStyles from '../../../utils/cssStyles';
import { fDateTime } from '../../../utils/formatTime';
// components
import { IconButtonAnimate } from '../../../components/animate';
import Avatar from '../../../components/Avatar';
import Iconify from '../../../components/Iconify';
import MenuPopover from '../../../components/MenuPopover';
import { addNewNoty, getNotifications, markNotiAsRead } from '../../../redux/slices/notification';
import { useDispatch, useSelector } from '../../../redux/store';
import { PATH_DASHBOARD } from '../../../routes/paths';

// hooks
import useAuth from '../../../hooks/useAuth';
import useLocales from '../../../hooks/useLocales';
import useWebSocket from '../../../hooks/useWS';
import { getCurentPendingTasks, markAsCompleted, markAsRead } from '../../../redux/slices/task';
import createAvatar from '../../../utils/createAvatar';
import { getListQuest } from '../../../redux/slices/quest';
// ----------------------------------------------------------------------

const LIMIT_NOTY_NUMBER = 10;
const BASE_URL_TASKS = 'api/v1/users/requests/';

// ----------------------------------------------------------------------

const colors = {
  green: '#00AB55',
  red: '#FF4842',
};
const keywords = {
  'thất bại': colors.red,
  'xóa lịch': colors.red,
  'không thành công': colors.red,
  'lên lịch': colors.green,
  'chỉnh sửa': colors.green,
  'cập nhật': colors.green,
  'thành công': colors.green,
  'thay đổi nội dung': colors.green,
  'tạo bài viết': colors.green,
  'tạo tin': colors.green,
  'tích hợp': colors.green,
  'sử dụng chức năng': colors.green,
};
// ----------------------------------------------------------------------

export default function NotificationsPopover() {
  const { translate } = useLocales('header.notification');
  const { enqueueSnackbar } = useSnackbar();

  const dispatch = useDispatch();

  const [newNoty, setNewNoty] = useState(false);

  const { bellNotiList, totalUnread } = useSelector((state) => state.noti);

  const { expiredAt } = useSelector((state) => state.dataExpiration);

  const navigate = useNavigate();

  const [open, setOpen] = useState(null);

  const renderedOnFirstTime = useRef(false);

  useEffect(() => {
    if (expiredAt && renderedOnFirstTime.current) {
      dispatch(getNotifications());
      getCurentPendingTasks(BASE_URL_TASKS, {});
      getListQuest({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expiredAt]);

  useEffect(() => {
    (async () => {
      dispatch(getNotifications());
      await getCurentPendingTasks(BASE_URL_TASKS, {});
      renderedOnFirstTime.current = true;
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!_.isEmpty(newNoty)) {
      dispatch(addNewNoty(newNoty));
      if (newNoty?.snackbar) {
        enqueueSnackbar(newNoty.description, { variant: newNoty.variant, autoHideDuration: 5000 });
      }
      // Check is idempotency request
      const requestData = newNoty?.data?.request_data;
      if (requestData) {
        markAsCompleted(requestData);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newNoty]);

  const { wsClient } = useWebSocket();

  const handleOpen = (event) => {
    if (totalUnread > 0) {
      // Mark as read all notifications
      dispatch(markNotiAsRead([]));
    }
    setOpen(event.currentTarget);
  };

  const handleClose = () => {
    setOpen(null);
  };

  const handleViewNotifications = () => {
    handleClose();
    navigate(PATH_DASHBOARD.user.notification);
  };

  useEffect(() => {
    if (wsClient) {
      wsClient.onmessage = (message) => {
        const dataFromServer = JSON.parse(message.data);
        if (dataFromServer) {
          console.log('Message receive:', dataFromServer);
          setNewNoty(dataFromServer);
        }
      };
    }
  }, [wsClient]);

  return (
    <>
      <IconButtonAnimate color={open ? 'primary' : 'default'} onClick={handleOpen} sx={{ width: 40, height: 40 }}>
        <Badge badgeContent={totalUnread} color="error">
          <Iconify icon="eva:bell-fill" width={20} height={20} />
        </Badge>
      </IconButtonAnimate>

      <MenuPopover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleClose}
        sx={{
          width: 360,
          p: 0,
          mt: 1.5,
          ml: 0.75,
          overflowY: 'auto',
          height: bellNotiList.length > LIMIT_NOTY_NUMBER / 2 ? '73%' : 'fit-content',
          '&::-webkit-scrollbar': {
            display: 'none',
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', py: 2, px: 2.5 }}>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="subtitle1">{translate('notification')}</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {totalUnread > 0 && translate('unread').replace('#', totalUnread)}
            </Typography>
          </Box>

          <Tooltip title={translate('all_notification')}>
            <IconButton color="default" onClick={handleViewNotifications}>
              <Iconify icon="basil:eye-solid" sx={{ color: '#000' }} />
            </IconButton>
          </Tooltip>
        </Box>
        <Divider sx={{ borderStyle: 'dashed' }} />
        <List disablePadding>
          {bellNotiList.map((notification) => (
            <NotificationItem key={notification?.id} notification={notification} handleClose={handleClose} />
          ))}
        </List>
      </MenuPopover>
    </>
  );
}

// ----------------------------------------------------------------------

NotificationItem.propTypes = {
  notification: PropTypes.any,
  handleClose: PropTypes.func,
};

function NotificationItem({ notification, handleClose }) {
  const dispatch = useDispatch();
  const { user } = useAuth();
  const { translate } = useLocales('dashboard.notification');
  const { currentTenant } = useSelector((state) => state.tenant);

  const { enqueueSnackbar } = useSnackbar();

  const theme = useTheme();

  const PRIMARY_MAIN = theme?.palette?.primary.main;

  const navigate = useNavigate();

  // Is idempotency request notification
  const requestData = notification?.data?.request_data;
  const idempotencyRequest = requestData && requestData?.result_data?.asset_type !== 'new_asset' ? requestData : null;

  const colorKeywordText = (verb) => {
    if (!verb) {
      return verb;
    }

    const keys = Object.keys(keywords);
    const getObject = keys.reduce(
      (result, curr, index) => {
        const indexKey = result.textReplaced.indexOf(curr);
        if (indexKey !== -1) {
          const colorText = (
            <Typography
              className="colored"
              key={`${curr}${index}`}
              display="inline"
              variant="subtitle2"
              color={keywords[curr]}
            >
              {curr}
              &nbsp;
            </Typography>
          );
          result.textReplaced = result.textReplaced.replaceAll(curr, `|${index}|`);
          result.results.push({
            index,
            component: colorText,
          });
        }
        return { ...result };
      },
      { textReplaced: verb, results: [] }
    );

    const { textReplaced, results } = getObject;
    const result = textReplaced.split(' ');
    return result.map((item) => {
      if (/^\|\d+\|$/g.test(item)) {
        const index = +item.split('|')[1];
        const instance = results.find((item) => item?.index === index);
        return instance.component;
      }
      return `${item} `;
    });
  };

  const renderContent = (notification, user) => {
    const areYou = notification?.actor_name === user?.name || notification?.actor_name === user?.email;

    const getUserInfo = () => {
      let description = (
        <Typography key={notification?.id} display="inline" variant="string">
          {colorKeywordText(notification?.verb)}
          <Typography display="inline" variant="string" fontWeight={'bold'}>
            {notification?.target_name}
          </Typography>
        </Typography>
      );

      if (notification?.data?.variant === 'error') {
        const reason = (
          <Typography
            key={`reason|${notification?.timestamp}|`}
            display="inline"
            variant="subtitle2"
            color={colors.red}
          >
            lý do:
          </Typography>
        );
        description = [description, ', ', reason, ` ${notification?.description}`];
      }

      return {
        name: areYou ? translate('you') : _.get(notification?.actor_name, ''),
        avatar: areYou ? user?.image : currentTenant?.config?.logo || '/logo/uptin.png',
        description,
      };
    };

    const title = (
      <Typography variant="subtitle2">
        {getUserInfo().name}
        <Typography component="span" variant="body2" sx={{ color: 'text.secondary' }}>
          &nbsp;{getUserInfo().description}
        </Typography>
      </Typography>
    );

    return {
      avatar: areYou ? user?.image : currentTenant?.config?.logo || '/logo/uptin.png',
      title,
    };
  };

  const { avatar, title } = renderContent(notification, user);

  const onClickNoty = async (event, notyId) => {
    if (idempotencyRequest) {
      if (idempotencyRequest?.result_data?.action === 'auto-connect') {
        navigate(`${PATH_DASHBOARD.account.list}?social=${idempotencyRequest?.result_data?.backend}`);
        markAsRead(idempotencyRequest?.request_code);
        return '';
      }
      // Snackbar or redirect
      const {
        success,
        asset_type: assetType,
        asset_uuid: assetUUID,
        new_content: newContent,
      } = idempotencyRequest?.result_data;
      if (success) {
        const typeMapping = {
          BĐS: 'realEdit',
          MXH: 'socialEdit',
          WEB: 'webEdit',
        };
        navigate(PATH_DASHBOARD.post[typeMapping[assetType]](assetUUID));
        markAsRead(idempotencyRequest?.request_code);
      } else {
        markAsRead(idempotencyRequest?.request_code);
        enqueueSnackbar(newContent, { variant: 'error' });
      }
      handleClose();
      return '';
    }

    if (notification.data.is_read || Object.prototype.hasOwnProperty.call(notification.data, 'expiration')) {
      return '';
    }

    return dispatch(markNotiAsRead([notyId]));
  };

  return (
    <ListItemButton
      sx={{
        py: 1.5,
        px: 2.5,
        mt: '1px',
        ...((!notification.data.is_read || Object.prototype.hasOwnProperty.call(notification.data, 'expiration')) && {
          bgcolor: 'action.selected',
        }),
        ...(idempotencyRequest?.result_data?.success && {
          ...cssStyles(theme).bgBlur({ color: PRIMARY_MAIN, opacity: 0.8 }),
          '*:not(.colored)': {
            color: '#fff !important',
          },
          '&:hover': {
            bgcolor: 'action.selected',
            '*:not(.colored)': {
              color: '#000 !important',
            },
          },
        }),
      }}
      onClick={(event) => onClickNoty(event, notification.id)}
    >
      <ListItemAvatar>
        <Avatar
          src={avatar}
          alt={notification?.actor_name}
          color={notification?.actor_name ? createAvatar(notification?.actor_name).color : 'default'}
        >
          {createAvatar(notification?.actor_name).name}
        </Avatar>
      </ListItemAvatar>
      <ListItemText
        primary={title}
        secondary={
          <Typography
            variant="caption"
            sx={{
              mt: 0.5,
              display: 'flex',
              alignItems: 'center',
              color: 'text.disabled',
            }}
          >
            <Iconify icon="eva:clock-outline" sx={{ mr: 0.5, width: 16, height: 16 }} />
            {fDateTime(new Date(notification.timestamp))}
          </Typography>
        }
      />
    </ListItemButton>
  );
}
